import { useState } from "react";
import { useGetLocale, useNavigation, useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import { CheckIcon } from "@phosphor-icons/react";
import { Button, radius } from "@libertify/ui";
import styled from "styled-components";
import { useTeacherOnboarding } from "./teacherOnboardingContext";
import { I18N_PREFIX, TITLE_FONT } from "./constants";
import {
  ARCHETYPES,
  archetypeCode,
  axLabel,
  getArchetype,
  getProfileDims,
  illPos,
  settingCode,
  structPos,
  voicePos,
} from "./archetypes";
import type { Lang } from "./archetypes";

const R = `${I18N_PREFIX}.reveal`;

const bandLabel = (pos: number, lo: string, hi: string, balanced: string) => {
  if (pos < 33) return lo;
  if (pos > 66) return hi;
  return balanced;
};

const FinishStep = () => {
  const t = useTranslate();
  const getLocale = useGetLocale();
  const { list } = useNavigation();
  const { answers, onComplete } = useTeacherOnboarding();

  const lang: Lang = (getLocale() ?? "en") === "fr" ? "fr" : "en";
  const myCode = archetypeCode(answers);
  const [viewedCode, setViewedCode] = useState(myCode);
  const isOwn = viewedCode === myCode;
  const sc = settingCode(answers);
  const vArch = getArchetype(viewedCode);
  const vName = lang === "fr" ? vArch.fr : vArch.en;
  const ident = lang === "fr" ? vArch.identFr : vArch.identEn;
  const aff = lang === "fr" ? vArch.affFr : vArch.affEn;
  const balanced = t(`${R}.balanced`);

  const settingLabel = () => {
    const d = sc.startsWith("X")
      ? t(`${R}.setting.expert`)
      : t(`${R}.setting.essential`);
    const a = sc.endsWith("A")
      ? t(`${R}.setting.empowers`)
      : t(`${R}.setting.guide`);
    return `${d} · ${a}`;
  };

  const voiceWord = answers.voice1
    ? t(`${R}.voiceWords.${answers.voice1}`)
    : "—";

  const bars = [
    { key: "depth", pos: answers.depth, value: bandLabel(answers.depth, t(`${R}.axes.depth.lo`), t(`${R}.axes.depth.hi`), balanced) },
    { key: "structure", pos: structPos(answers), value: bandLabel(structPos(answers), t(`${R}.axes.structure.lo`), t(`${R}.axes.structure.hi`), balanced) },
    { key: "illustration", pos: illPos(answers), value: bandLabel(illPos(answers), t(`${R}.axes.illustration.lo`), t(`${R}.axes.illustration.hi`), balanced) },
    { key: "voice", pos: voicePos(answers), value: voiceWord },
    { key: "autonomy", pos: answers.autonomy, value: bandLabel(answers.autonomy, t(`${R}.axes.autonomy.lo`), t(`${R}.axes.autonomy.hi`), balanced) },
  ];

  const profileDims = getProfileDims(answers, viewedCode, isOwn);

  return (
    <Flex vertical align="center" gap={20} style={{ width: "100%" }}>
      <div aria-hidden style={{ fontSize: 40, lineHeight: 1 }}>
        ✨
      </div>
      <Typography.Title
        level={3}
        style={{ margin: 0, textAlign: "center", fontFamily: TITLE_FONT }}
      >
        {t(`${R}.title`)}
      </Typography.Title>
      <Typography.Paragraph
        type="secondary"
        style={{ margin: 0, maxWidth: 540, textAlign: "center" }}
      >
        {t(`${R}.subtitle`)}
      </Typography.Paragraph>

      {/* DNA bars */}
      <Flex vertical gap={11} style={{ width: "100%", maxWidth: 600 }}>
        {bars.map((b) => (
          <DnaRow key={b.key}>
            <Flex justify="space-between" align="baseline">
              <DnaName>{t(`${R}.axes.${b.key}.name`)}</DnaName>
              <DnaValue>{b.value}</DnaValue>
            </Flex>
            <DnaBar>
              <DnaFill style={{ width: `${b.pos}%` }} />
            </DnaBar>
            <DnaQ>{t(`${R}.axes.${b.key}.q`)}</DnaQ>
          </DnaRow>
        ))}
      </Flex>

      {answers.guardrail.trim() && (
        <SeedNote $accent style={{ maxWidth: 600 }}>
          {t(`${R}.guardLabel`)} « {answers.guardrail.trim()} »
        </SeedNote>
      )}

      {/* Archetype explorer */}
      <AllCap>{t(`${R}.archAll`)}</AllCap>
      <ArchGrid>
        {ARCHETYPES.map((a) => {
          const me = a.code === myCode;
          const viewing = a.code === viewedCode;
          return (
            <ArchCard
              key={a.code}
              type="button"
              $me={me}
              $viewing={viewing && !me}
              onClick={() => setViewedCode(a.code)}
            >
              {me && <YouBadge>{t(`${R}.you`)}</YouBadge>}
              <Flex justify="space-between" align="baseline" gap={6}>
                <ArchName>{lang === "fr" ? a.fr : a.en}</ArchName>
                <ArchCode>{a.code}</ArchCode>
              </Flex>
              <ArchAx>{axLabel(a.code, lang)}</ArchAx>
            </ArchCard>
          );
        })}
      </ArchGrid>
      <GridHint>{t(`${R}.archHint`)}</GridHint>

      <Headline $exploring={!isOwn}>
        {!isOwn && (
          <BackLink type="button" onClick={() => setViewedCode(myCode)}>
            {t(`${R}.archBack`)}
          </BackLink>
        )}
        <Eyebrow>{isOwn ? t(`${R}.archEyebrow`) : t(`${R}.archOther`)}</Eyebrow>
        <BigName>
          {vName} <BigCode>{isOwn ? `${myCode} · ${sc}` : viewedCode}</BigCode>
        </BigName>
        {isOwn && (
          <Setting>
            {t(`${R}.archSetting`)} {settingLabel()}
          </Setting>
        )}
        <Ident>{ident}</Ident>
      </Headline>

      <ProfCap>
        {isOwn
          ? t(`${R}.profCap`)
          : t(`${R}.profCapOther`, { name: vName })}
      </ProfCap>
      <Flex vertical gap={10} style={{ width: "100%", maxWidth: 600 }}>
        {profileDims.map(({ dimension, letter }) => {
          const dimKey = `creatorDna.dimensions.${dimension}.${letter}`;
          return (
            <ProfRow key={dimension}>
              <ProfTick>
                <CheckIcon weight="bold" size={12} />
              </ProfTick>
              <div>
                <ProfHead>{t(`${dimKey}.title`)}</ProfHead>
                <ProfBody>{t(`${dimKey}.body`)}</ProfBody>
              </div>
            </ProfRow>
          );
        })}
      </Flex>

      <Aff>{aff}</Aff>
      <Foot>{t(`${R}.archFoot`)}</Foot>

      <Button
        variant="primary"
        size="l"
        onClick={() => (onComplete ? onComplete() : list("media_projects"))}
      >
        {t(`${R}.cta`)}
      </Button>
    </Flex>
  );
};

export default FinishStep;

// ─── Styled components ───

// Display serif italic for archetype names + affirmation (matches the
// prototype's Newsreader/Georgia treatment).
const SERIF = "Georgia, 'Times New Roman', serif";

const DnaRow = styled.div`
  border: 1px solid ${({ theme }) => theme.colorBorder};
  border-radius: ${radius.box}px;
  padding: 15px 17px;
  background: ${({ theme }) => theme.colorBgContainer};
`;

const DnaName = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colorText};
`;

const DnaValue = styled.span`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colorPrimary};
`;

const DnaBar = styled.div`
  height: 6px;
  margin-top: 7px;
  border-radius: ${radius.pill}px;
  background: ${({ theme }) => theme.colorFillSecondary};
  overflow: hidden;
`;

const DnaFill = styled.div`
  height: 100%;
  border-radius: ${radius.pill}px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colorPrimary},
    ${({ theme }) => theme.colorPrimaryBorder}
  );
  transition: width 0.5s ease;
`;

const DnaQ = styled.div`
  margin-top: 8px;
  font-size: 11.5px;
  font-style: italic;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const SeedNote = styled.div<{ $accent?: boolean }>`
  width: 100%;
  border: 1px solid
    ${({ theme, $accent }) =>
      $accent ? theme.colorPrimaryBorder : theme.colorBorder};
  background: ${({ theme, $accent }) =>
    $accent ? theme.colorPrimaryBg : theme.colorFillQuaternary};
  border-radius: ${radius.box}px;
  padding: 13px 15px;
  font-size: 12.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colorText};
`;

const AllCap = styled.div`
  text-align: center;
  max-width: 560px;
  font-size: 13.5px;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const ArchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  width: 100%;
  max-width: 600px;

  @media (width <= 760px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (width <= 520px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ArchCard = styled.button<{ $me: boolean; $viewing: boolean }>`
  position: relative;
  text-align: left;
  min-height: 64px;
  padding: 12px 13px;
  border-radius: ${radius.box}px;
  cursor: pointer;
  transition: 0.18s;
  background: ${({ theme, $me }) =>
    $me ? theme.colorPrimaryBg : theme.colorBgContainer};
  border: 1px solid
    ${({ theme, $me, $viewing }) => {
      if ($me) return theme.colorPrimary;
      if ($viewing) return theme.colorTextTertiary;
      return theme.colorBorder;
    }};

  &:hover {
    border-color: ${({ theme }) => theme.colorPrimary};
    transform: translateY(-1px);
  }
`;

const YouBadge = styled.span`
  position: absolute;
  top: -9px;
  left: 11px;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
  background: ${({ theme }) => theme.colorPrimary};
  border-radius: ${radius.pill}px;
  padding: 2px 9px;
`;

const ArchName = styled.span`
  font-family: ${SERIF};
  font-style: italic;
  font-size: 15.5px;
  line-height: 1.15;
  color: ${({ theme }) => theme.colorText};
`;

const ArchCode = styled.span`
  flex: none;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colorTextSecondary};
  background: ${({ theme }) => theme.colorFillSecondary};
  border-radius: 6px;
  padding: 2px 6px;
`;

const ArchAx = styled.div`
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const GridHint = styled.div`
  text-align: center;
  font-size: 12px;
  font-style: italic;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const Headline = styled.div<{ $exploring: boolean }>`
  width: 100%;
  max-width: 600px;
  text-align: center;
  border: 1px solid
    ${({ theme, $exploring }) =>
      $exploring ? theme.colorBorder : theme.colorPrimaryBorder};
  background: ${({ theme, $exploring }) =>
    $exploring ? theme.colorFillQuaternary : theme.colorPrimaryBg};
  border-radius: ${radius.box}px;
  padding: 20px 22px;
`;

const BackLink = styled.button`
  display: block;
  margin-bottom: 8px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  text-decoration: underline;
  color: ${({ theme }) => theme.colorPrimary};
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colorPrimary};
  margin-bottom: 8px;
`;

const BigName = styled.div`
  font-family: ${SERIF};
  font-style: italic;
  font-size: 30px;
  line-height: 1.1;
  color: ${({ theme }) => theme.colorText};
`;

const BigCode = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colorPrimary};
  background: ${({ theme }) => theme.colorBgContainer};
  border: 1px solid ${({ theme }) => theme.colorPrimaryBorder};
  border-radius: 8px;
  padding: 2px 9px;
  margin-left: 8px;
  vertical-align: middle;
  letter-spacing: 0.04em;
`;

const Setting = styled.div`
  margin-top: 9px;
  font-size: 13px;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const Ident = styled.p`
  max-width: 560px;
  margin: 14px auto 0;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colorPrimaryBorder};
  font-size: 15px;
  font-weight: 500;
  line-height: 1.55;
  color: ${({ theme }) => theme.colorText};
`;

const ProfCap = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const ProfRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: ${({ theme }) => theme.colorBgContainer};
  border: 1px solid ${({ theme }) => theme.colorBorder};
  border-radius: ${radius.box}px;
  padding: 13px 15px;
`;

const ProfTick = styled.div`
  flex: none;
  width: 22px;
  height: 22px;
  margin-top: 1px;
  border-radius: ${radius.circle};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colorPrimary};
  background: ${({ theme }) => theme.colorPrimaryBg};
`;

const ProfHead = styled.span`
  display: block;
  margin-bottom: 3px;
  font-weight: 700;
  font-size: 14px;
  color: ${({ theme }) => theme.colorText};
`;

const ProfBody = styled.span`
  display: block;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colorText};
`;

const Aff = styled.p`
  max-width: 540px;
  margin: 8px auto 0;
  text-align: center;
  font-family: ${SERIF};
  font-style: italic;
  font-size: 17px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colorPrimary};
`;

const Foot = styled.p`
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colorTextSecondary};
`;
